import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthUser } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useUserRole";

const MAX_VIEW_COUNT = 5;

interface FeatureView {
  feature_key: string;
  view_count: number;
}

export function useNewFeatureBadge() {
  const { data: user } = useAuthUser();
  const { isAdmin } = useIsAdmin();
  const queryClient = useQueryClient();

  // Busca todas as visualizações do usuário
  const { data: featureViews = [] } = useQuery({
    queryKey: ['feature-views', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('user_feature_views')
        .select('feature_key, view_count')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching feature views:', error);
        return [];
      }

      return data as FeatureView[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
  });

  // Mutation para marcar como visualizado
  const markAsViewedMutation = useMutation({
    mutationFn: async (featureKey: string) => {
      if (!user?.id) return;

      // Primeiro, busca se já existe
      const { data: existing } = await supabase
        .from('user_feature_views')
        .select('view_count')
        .eq('user_id', user.id)
        .eq('feature_key', featureKey)
        .maybeSingle();

      if (existing) {
        // Incrementa o contador existente (máximo MAX_VIEW_COUNT)
        const newCount = Math.min(existing.view_count + 1, MAX_VIEW_COUNT);
        await supabase
          .from('user_feature_views')
          .update({ 
            view_count: newCount,
            last_viewed_at: new Date().toISOString()
          })
          .eq('user_id', user.id)
          .eq('feature_key', featureKey);
      } else {
        // Cria novo registro
        await supabase
          .from('user_feature_views')
          .insert({
            user_id: user.id,
            feature_key: featureKey,
            view_count: 1,
            last_viewed_at: new Date().toISOString(),
          });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-views', user?.id] });
    },
  });

  // Verifica se deve mostrar o badge "NOVO" para uma feature
  const shouldShowBadge = (featureKey: string, maxViews: number = MAX_VIEW_COUNT): boolean => {
    if (isAdmin) return false;
    if (!user?.id) return false;
    
    const view = featureViews.find(v => v.feature_key === featureKey);
    return !view || view.view_count < maxViews;
  };

  // Marca uma feature como visualizada
  const markAsViewed = (featureKey: string) => {
    if (!user?.id || !featureKey) return;
    markAsViewedMutation.mutate(featureKey);
  };

  return {
    shouldShowBadge,
    markAsViewed,
    isLoading: markAsViewedMutation.isPending,
  };
}
